import type { ReactElement } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import styled from 'styled-components';
import { Tag } from '../../../components';
import type { Installer } from '../../../model';

interface Props {
  installers?: Installer[];
  onUpdate: (installer: Installer) => void;
  onDelete: (installer: Installer) => void;
}

const COLUMNS =
  'minmax(90px, 0.7fr) minmax(220px, 1.6fr) minmax(220px, 1.6fr) 110px 110px 30px';

const Scroll = styled.div`
  overflow-x: auto;
  padding-bottom: 4px;
`;

const Inner = styled.div`
  min-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const HeaderRow = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 13px;
  align-items: center;
  background: rgba(17, 27, 51, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 14px 16px;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(176, 186, 209, 0.55);
`;

const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 13px;
  align-items: center;
  background: rgba(17, 27, 51, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 14px 16px;
  color: var(--white);

  .version {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .version .name {
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .url {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: var(--white35);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .exp,
  .date {
    font-size: 13px;
    color: var(--white60);
    white-space: nowrap;
  }
`;

const OptionsToggle = styled(Dropdown.Toggle)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--white60);

  &::after {
    content: none;
  }

  &:hover,
  &:focus {
    box-shadow: none;
    color: var(--white);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const OptionsMenu = styled(Dropdown.Menu)`
  min-width: 186px;
  background: rgb(15, 24, 48);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 16px 40px;
  padding: 6px;

  .dropdown-item {
    background: transparent;
    color: rgba(213, 221, 238, 0.92);
    font-size: 13px;
    padding: 9px 11px;
    border-radius: 7px;

    &:hover,
    &:focus,
    &:active {
      background: rgba(255, 255, 255, 0.06);
      color: rgba(213, 221, 238, 0.92);
    }

    &.unlink {
      color: rgb(255, 138, 147);

      &:hover,
      &:focus,
      &:active {
        color: rgb(255, 138, 147);
        background: rgba(255, 80, 95, 0.1);
      }
    }
  }

  .dropdown-divider {
    height: 1px;
    margin: 5px 4px;
    border: none;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const fmtDate = (date: Date): string =>
  date.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: '2-digit' }).replaceAll('/', '-');

// Version badge parsed from the installer filename, e.g. "…_3_4_31_…" → "v3.4.31".
const installerVersion = (name: string): string | undefined => {
  const semver = /(\d+)_(\d+)_(\d+)/.exec(name);

  if (semver) {
    return `v${semver[1]}.${semver[2]}.${semver[3]}`;
  }

  const first = /\d+/.exec(name);

  return first ? first[0] : undefined;
};

export const Table = ({ installers, onUpdate, onDelete }: Props): ReactElement => (
  <Scroll className='cc-scroll'>
    <Inner>
      <HeaderRow>
        <span>Software</span>
        <span>Version Name</span>
        <span>URL</span>
        <span>Signed Exp.</span>
        <span>Date Added</span>
        <span />
      </HeaderRow>

      {installers?.map((installer) => {
        const version = installerVersion(installer.versionName);

        return (
          <Row key={installer.id}>
            <span>
              <Tag
                size='small'
                className={
                  installer.software === 'EMS'
                    ? 'cc-azure'
                    : installer.software === 'LCS'
                    ? 'cc-blue'
                    : 'cc-neutral'
                }
              >
                {installer.software}
              </Tag>
            </span>

            <div className='version'>
              {version !== undefined && (
                <Tag
                  size='small'
                  className='cc-green'
                  style={{ fontFamily: "'JetBrains Mono', monospace", border: 'none' }}
                >
                  {version}
                </Tag>
              )}
              <span className='name'>{installer.versionName}</span>
            </div>

            <span className='url'>{installer.url}</span>

            <span className='exp'>{installer.expirationTime} min</span>

            <span className='date'>{fmtDate(installer.createdAt)}</span>

            <Dropdown alignRight>
              <OptionsToggle forwardedAs='button'>
                <i className='far fa-ellipsis-v' />
              </OptionsToggle>
              <OptionsMenu popperConfig={{ strategy: 'fixed' }}>
                <Dropdown.Item
                  onClick={(): void => {
                    onUpdate(installer);
                  }}
                >
                  Modify
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  className='unlink'
                  onClick={(): void => {
                    onDelete(installer);
                  }}
                >
                  Delete
                </Dropdown.Item>
              </OptionsMenu>
            </Dropdown>
          </Row>
        );
      })}
    </Inner>
  </Scroll>
);

